import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useQueryClient } from "@tanstack/react-query"
import {
    fetchAcquirerAgreementsForMerchant,
    fetchMerchantBankAccountsResult,
    MerchantAccountQuery,
} from "../merchantAccountApi"
import { SupplierMerchantDetailsResponse } from "../../../../../supplier/admin/merchant/merchants/supplierMerchantsApi"
import {
    AcquiringMerchantDetails,
    BankAccountDetails,
} from "../../../../../supplier/admin/merchant/merchants/supplierMerchantsTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    Agreement,
    WellknownAgreementType,
} from "../../../../../supplier/admin/settings/posAcquirer/SupplierAdminPOSTypes"
import {
    FetchAcquirerAgreementsResponse,
    FetchBankAccountsResponse,
} from "../../../../../supplier/admin/settings/posAcquirer/SupplierAdminPOSApi"

const selectMerchantAgreements = (data: FetchAcquirerAgreementsResponse) => data?.agreements || []
const selectBankAccounts = (data: FetchBankAccountsResponse) => data?.bankAccounts || []

export const useMerchantPaymentsData = () => {
    const { merchantId } = useMerchant()
    const queryClient = useQueryClient()

    const merchantData = queryClient.getQueryData<SupplierMerchantDetailsResponse>([
        MerchantAccountQuery.DETAILS,
        { merchantId },
    ])

    const acquiringData = merchantData?.merchant?.acquiringDetails
    const acquiring = acquiringData && "acquiring" in acquiringData ? acquiringData.acquiring : undefined

    const bankAccountsResult = useApiQuery(fetchMerchantBankAccountsResult, {
        queryName: MerchantAccountQuery.BANK_ACCOUNTS,
        dependencies: {
            merchantId,
        },
        select: selectBankAccounts,
    })

    const merchantAgreementsResult = useApiQuery(fetchAcquirerAgreementsForMerchant, {
        queryName: MerchantAccountQuery.AGREEMENTS,
        dependencies: {
            merchantId,
        },
        select: selectMerchantAgreements,
    })

    const isLoading = bankAccountsResult.isLoading || merchantAgreementsResult.isLoading

    const filteredArray = merchantAgreementsResult.data
        ? merchantAgreementsResult.data.filter(
              (agreement: Agreement) => agreement.type === WellknownAgreementType.ADYEN_POS,
          )
        : []

    const isBankAccount = !!bankAccountsResult.data?.length

    const acquiringDetails: AcquiringMerchantDetails = acquiring
        ? {
              acquiring: {
                  type: acquiring.type,
                  salesChannels: acquiring.salesChannel,
                  defaultCurrencyCode: acquiring.defaultCurrencyCode,
                  bankAccount: acquiring.bankAccount,
                  industryCodes: acquiring.industryCodes.map((code) => code.code),
              },
          }
        : {}

    const bankAccountDetails: BankAccountDetails = {
        status: acquiring?.bankAccount?.status,
        bankName: acquiring?.bankAccount?.bankName,
        accountIdentifier: acquiring?.bankAccount?.accountIdentifier,
    }

    return {
        isLoading,
        status: filteredArray[0]?.adyenAgreement?.status,
        acquiringDetails,
        bankAccountDetails,
        isBankAccount,
    }
}
